import { withTheme } from '@emotion/react';
import classNames from 'classnames';
import { Record as ImmutableRecord } from 'immutable';
// Core
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import compose from 'utils/compose';

import store from 'app-components/utils/redux-store';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { trackSeriesSubtabClick } from 'app-components/utils/core';
import { useBillScoutSetupState } from 'app-components/hooks/billScoutSetup';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';
import MenuItem from '@mui/material/MenuItem';

// MUI Icons
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

// Data
import { ViewStateEnum, computeViewState } from 'containers/OverviewPage/components/helpers';
import type { AppDispatch, RootState } from 'store';

// Components
import { showRefundEditDialog } from 'components/Dialogs/RefundEditDialog';
import { showAddScheduledTransactionDialog } from 'components/Dialogs/ScheduledTransactionAddDialog/actions';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import QDialogs from 'components/QDialogs';
import SearchBox from 'components/SearchBox';
import UIState from 'components/UIState';
import DevMenuButton from 'components/DevMenuButton';
import BillScoutIntro from 'components/BillScoutIntro/BillScoutIntro';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';
import potentialRecurrings from 'containers/App/DevMenu/potentialRecurrings';

// Assets
import underConstructionImage from 'assets/illustrations/Empty-box-disabled.png';

import ZeroStateView from 'components/ZeroStateView';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import RecurringRow from './RecurringRow';
import styles from './styles';

const typeEnums = scheduledTransactionsTypes.STTypeEnum;
const allValue = 'all';
const completedValue = 'COMPLETED';
const suggestedValue = 'SUGGESTED';

// Maps this page's persisted tab values onto the canonical shared series filters.
const seriesFiltersByTabValue: Record<string, scheduledTransactionsUtils.SeriesFilter> = {
  [allValue]: 'ALL',
  [typeEnums.BILL]: 'BILL',
  [typeEnums.SUBSCRIPTION]: 'SUBSCRIPTION',
  [typeEnums.INCOME]: 'INCOME',
  [typeEnums.TRANSFER]: 'TRANSFER',
  [completedValue]: 'CANCELED',
  [suggestedValue]: 'SUGGESTED',
};

const searchHeader = (filterTab: string): string => {
  switch (filterTab) {
    case allValue:
      return 'All Active';
    case typeEnums.BILL:
      return 'Bills';
    case typeEnums.SUBSCRIPTION:
      return 'Subscriptions';
    case typeEnums.INCOME:
      return 'Income';
    case typeEnums.TRANSFER:
      return 'Transfers';
    case completedValue:
      return 'Canceled';
    case suggestedValue:
      return 'Suggested';
    default:
      return 'transactions';
  }
};

const buttonText = (filterTab: string, defaultString?: string): string => {
  switch (filterTab) {
    case allValue:
    case completedValue:
    case suggestedValue:
      return defaultString ?? 'Series';
    case typeEnums.BILL:
      return 'Bill';
    case typeEnums.SUBSCRIPTION:
      return 'Subscription';
    case typeEnums.INCOME:
      return 'Income';
    case typeEnums.TRANSFER:
      return 'Transfer';
    default:
      return 'Bill';
  }
};

type RecurringProps = {
  addScheduledTransaction: (args?: { dialogType: string }) => void;
  classes: Record<string, string>;
  createRefund: () => void;
  defaultButtonString?: string;
  dispatchDoAccountDiscoveryAction: () => void;
  dispatchGetScheduledTransactionsAction: () => void;
  eligibleBillScoutIntro?: boolean;
  incomeCoaIds: scheduledTransactionsUtils.CoaIdSet;
  isDataEmpty?: boolean;
  isEnabled?: boolean;
  isLoading?: boolean;
  loadPending?: boolean;
  noDependencies?: boolean;
  scheduledTransactions: ReturnType<typeof scheduledTransactionsSelectors.getScheduledTransactions>;
  schTxnsSearchObject: {
    get: (id: string | null | undefined) => Record<string, string> | undefined;
  };
  setUIState: (state: { filterTab: string } | null) => void;
  settingsPage?: boolean;
  showCreateRefundButton?: boolean;
  uiState: { filterTab: string };
};

// TODO: Add credit card payments AND change the selectors so that credit card payments are an acceptable type (Once credit cards are added)
export const Recurring = (props: RecurringProps): React.JSX.Element => {
  const {
    addScheduledTransaction,
    classes,
    createRefund,
    defaultButtonString,
    dispatchDoAccountDiscoveryAction,
    dispatchGetScheduledTransactionsAction,
    eligibleBillScoutIntro,
    incomeCoaIds,
    isDataEmpty,
    isEnabled,
    isLoading,
    loadPending,
    noDependencies,
    scheduledTransactions,
    schTxnsSearchObject,
    setUIState,
    settingsPage,
    showCreateRefundButton,
    uiState,
  } = props;

  const [globalSearch, setGlobalSearch] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isLoading) {
      dispatchGetScheduledTransactionsAction();
    }

    // Fix bug where it ALWAYS displays the txn again when changing tabs
    if (!window.location.href.includes('Recurring/')) {
      setUIState(null);
    }

    return () => {
      setUIState({ filterTab: allValue });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const txnSearch = (scheduledtxn: scheduledTransactionsTypes.ScheduledTransaction): boolean => {
    if (!globalSearch) {
      return true;
    }

    const schTxn = schTxnsSearchObject.get(scheduledtxn.id);
    return Boolean(
      schTxn &&
        Object.values(schTxn).some((value) => typeof value === 'string' && value.toLowerCase().includes(globalSearch)),
    );
  };

  const addDialog = () => {
    let dialogProps: { dialogType: string } | undefined = { dialogType: uiState.filterTab }; // Filter by the selected tab
    if (
      dialogProps.dialogType === completedValue ||
      dialogProps.dialogType === allValue ||
      dialogProps.dialogType === suggestedValue
    ) {
      dialogProps = undefined; // Use the default value
    }
    addScheduledTransaction(dialogProps);
  };

  const renderAppBar = () => {
    const tabClasses = {
      root: classes.tab,
      selected: classes.selectedTab,
    };

    return (
      <div className={classes.appBar}>
        <div className={classes.appBarGridContainer}>
          <Tabs
            value={uiState.filterTab}
            onChange={(_event, value: string) => {
              const seriesFilter = seriesFiltersByTabValue[value];
              if (seriesFilter) {
                trackSeriesSubtabClick(seriesFilter);
              }
              setUIState({ filterTab: value });
            }}
            variant='scrollable'
            scrollButtons='auto'
            slots={{
              scrollButtons: (scrollProps) => {
                const isLeft = scrollProps.direction === 'left';
                const clsNames = classNames(
                  classes.tabBarArrowButtons,
                  isLeft ? classes.arrowLeft : classes.arrowRight,
                );

                if (!scrollProps.visible) {
                  return <div className={clsNames} />;
                }

                return (
                  <ButtonBase component='div' className={clsNames} onClick={scrollProps.onClick}>
                    {isLeft ? <KeyboardArrowLeft fontSize='small' /> : <KeyboardArrowRight fontSize='small' />}
                  </ButtonBase>
                );
              },
            }}
            textColor='primary'
            className={classes.mainTab}
            classes={{
              flexContainer: classes.tabBar,
            }}
          >
            <Tab label='All Active' id='all-tab' value={allValue} classes={tabClasses} />
            <Tab label='Bills' id='bills-tab' value={typeEnums.BILL} classes={tabClasses} />
            <Tab label='Subscriptions' id='subscription-tab' value={typeEnums.SUBSCRIPTION} classes={tabClasses} />
            <Tab label='Income' id='income-tab' value={typeEnums.INCOME} classes={tabClasses} />
            <Tab label='Transfers' id='transfer-tab' value={typeEnums.TRANSFER} classes={tabClasses} />
            <Tab label='Canceled' id='canceled-tab' value={completedValue} classes={tabClasses} />
            <Tab label='Suggested' id='suggested-tab' value={suggestedValue} classes={tabClasses} />
          </Tabs>
        </div>
      </div>
    );
  };

  const renderRecurringRows = (seriesFilter: scheduledTransactionsUtils.SeriesFilter) => (
    <>
      {scheduledTransactions
        .filter(
          (schTxn: scheduledTransactionsTypes.ScheduledTransaction) =>
            Boolean(seriesFilter) && scheduledTransactionsUtils.seriesMatchesFilter(schTxn, seriesFilter, incomeCoaIds),
        )
        .filter(txnSearch)
        .toIndexedSeq()
        .map((schTxn: scheduledTransactionsTypes.ScheduledTransaction, index: number) => (
          <RecurringRow key={schTxn.clientId || schTxn.id} model={schTxn} index={index} />
        ))}
    </>
  );

  const seriesFilter = seriesFiltersByTabValue[uiState.filterTab];

  let view: ReturnType<typeof computeViewState> | typeof suggestedValue = computeViewState(
    () => isLoading,
    () => isDataEmpty,
    noDependencies,
    loadPending,
    !isEnabled,
  );
  if (view === ViewStateEnum.EMPTY && eligibleBillScoutIntro) {
    view = 'SUGGESTED';
  }

  return (
    <Typography component='div' className={settingsPage ? classes.floorStyle : classes.floorStyleUpcoming}>
      <SettingsHeader
        title={settingsPage ? 'All Recurring' : ''}
        primaryItem={
          view === ViewStateEnum.MAIN && (
            <div className={classes.buttonWrapper}>
              <DevMenuButton id='recurring-settings-dev-menu'>
                <MenuItem
                  onClick={() =>
                    dispatchRequestDialog({
                      method: 'GET',
                      path: '/scheduled-transactions',
                    })
                  }
                >
                  manual API Call
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    store?.dispatch(
                      scheduledTransactionsActions.batchScheduledTransactions(
                        scheduledTransactions
                          .toIndexedSeq()
                          .map((scheduledTransaction: scheduledTransactionsTypes.ScheduledTransaction) =>
                            scheduledTransactionsTypes.mkScheduledTransactionPatch({
                              clientId: scheduledTransaction.clientId,
                              id: scheduledTransaction.id,
                              isDeleted: true,
                            }),
                          )
                          .toArray(),
                        { undo: { userMessage: 'Deleted' } },
                      ),
                    );
                  }}
                >
                  delete all
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    store?.dispatch(
                      scheduledTransactionsActions.batchScheduledTransactions(
                        potentialRecurrings().map((scheduledTransaction) => {
                          // @ts-expect-error potentialRecurrings is JS fixture data that matches the scheduled transaction API shape.
                          return scheduledTransactionsTypes.mkScheduledTransaction(scheduledTransaction);
                        }),
                        { undo: { userMessage: 'Potential Recurrings Added' } },
                      ),
                    );
                  }}
                >
                  add potential recurrings
                </MenuItem>
              </DevMenuButton>
              {showCreateRefundButton && (
                <QButton variant='outlinedAdd' id='add-refund' onClick={createRefund}>
                  Refund
                </QButton>
              )}
              <QButton className={classes.addButton} variant='add' id='add-bill-button' onClick={addDialog}>
                {buttonText(uiState.filterTab, defaultButtonString)}
              </QButton>
            </div>
          )
        }
        secondaryItem={
          <SearchBox
            placeholder={`Search ${searchHeader(uiState.filterTab)}`}
            onSearch={(s?: string) => setGlobalSearch(s?.toLowerCase())}
            initialValue={globalSearch}
            autoSearch
            autoFocus
            collapsable
          />
        }
      />

      {view === ViewStateEnum.DISABLED && !isLoading && !loadPending && (
        <img
          alt='page is under construction'
          src={underConstructionImage}
          style={{
            display: 'block',
            width: '50%',
            marginTop: 40,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
      )}

      {view === ViewStateEnum.LOADING && <LoadingView style={{ margin: 40 }} />}

      {view === ViewStateEnum.LOAD_PENDING && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '80vh',
          }}
        >
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <ZeroStateView
              title='No Data'
              qIllustration='spendingPlanBills'
              description="We can't fetch your data from the server. Please try to refresh the page."
            />
          </Paper>
        </Box>
      )}

      {view === ViewStateEnum.ZERO && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '80vh',
          }}
        >
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <ZeroStateView
              animate={true}
              qIllustration='spendingPlanBills'
              description="You don't have any accounts set up."
              button='Add Account'
              size='small'
              buttonVariant='contained'
              onClick={() => dispatchDoAccountDiscoveryAction()}
            />
          </Paper>
        </Box>
      )}

      {view === ViewStateEnum.EMPTY && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '82vh',
          }}
        >
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <ZeroStateView
              size='large'
              animate={true}
              qIllustration='billCreditcard'
              title='No recurrings to display'
              description='Set up recurring transactions to see them here.'
              button='Add Recurring'
              buttonVariant='contained'
              onClick={addDialog}
            />
          </Paper>
        </Box>
      )}

      {view === ViewStateEnum.MAIN && (
        <Paper variant='outlined' className={settingsPage ? classes.root : classes.upcomingRoot}>
          {renderAppBar()}
          {renderRecurringRows(seriesFilter)}

          {eligibleBillScoutIntro && (
            <BillScoutIntro source='allSeriesTab' hasReminders={true} sx={{ maxWidth: 480, mx: 'auto' }} />
          )}
        </Paper>
      )}

      {view === 'SUGGESTED' && <BillScoutIntro source='allSeriesTab' sx={{ maxWidth: 480, mx: 'auto' }} />}
    </Typography>
  );
};

const mapStateToProps = (state: RootState) => {
  const scheduledTransactions = scheduledTransactionsSelectors.getScheduledTransactions(state);
  const allUpcomingTxns = transactionsSelectors.getAllUpcomingTransactions(state, scheduledTransactions);
  const featureFlags = featureFlagsSelectors.getFeatureFlags(state);

  return {
    isEnabled: featureFlags.get('scheduledTransactions'),
    isLoading: scheduledTransactionsSelectors.getIsLoading(state) || featureFlagsSelectors.getIsLoading(state),
    loadPending: scheduledTransactionsSelectors.getLoadPending(state),
    isDataEmpty: !scheduledTransactionsSelectors.hasUserVerifiedScheduledTransactions(state),
    noDependencies: accountsSelectors.getHasReportableAccounts(state),
    scheduledTransactions:
      scheduledTransactionsSelectors.getScheduledTransactionsSortedByPayee(state) ?? scheduledTransactions,
    allUpcomingTxns,
    incomeCoaIds: categoriesSelectors.getIncomeCOAIds(state),
    accountsById: accountsSelectors.getAccountsById(state),
    schTxnsSearchObject: scheduledTransactionsSelectors.schTxnsSearchNames(state),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  dispatchGetScheduledTransactionsAction: () => dispatch(scheduledTransactionsActions.getScheduledTransactions()),
  dispatchDoAccountDiscoveryAction: () =>
    dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'settings recurring txns' })),
  addScheduledTransaction: (args?: { dialogType: string }) =>
    dispatch(
      showAddScheduledTransactionDialog(
        args ? { dialogType: args.dialogType as scheduledTransactionsTypes.STType } : undefined,
      ),
    ),
  createRefund: () => dispatch(showRefundEditDialog({ id: 'refund-card' })),
});

const RecurringUIState = ImmutableRecord({
  filterTab: allValue,
});

const uiStateConfig = {
  persist: true,
  name: () => 'Recurring',
  state: () => new RecurringUIState(),
};

const ConnectedRecurring = compose(
  UIState(uiStateConfig),
  withTheme,
  QDialogs(),
  connect(mapStateToProps, mapDispatchToProps),
)(
  // `styles` comes from an untyped .js module, so its CSS values (e.g. `flexDirection: 'column'`)
  // are widened to `string` instead of the literal unions `CSSObject` expects. The old class
  // component masked this because `QPureComponent` (also untyped .js) made `Props` infer as
  // `any`; the function component infers `Props` correctly, which surfaces the mismatch here.
  withStyles(Recurring, styles as any),
);

// `mapStateToProps` can't call hooks, and `useBillScoutSetupState` reads a
// preference through `usePreferenceV2` (a hook) — so `showSetupSurfaces` is
// sourced here instead of in `mapStateToProps`, one level up.
export default function RecurringWithBillScoutSetup(props: React.ComponentProps<typeof ConnectedRecurring>) {
  const { showSetupSurfaces, isNewSetupEnabled, isControlArm } = useBillScoutSetupState();
  // Reqs Not Met has showSetupSurfaces=true whenever suggestions exist, but
  // BillScoutIntro itself renders null for that arm (see BillScoutIntro.tsx) —
  // this must match that gate exactly, or Reqs Not Met falls through to
  // nothing instead of the empty view.
  const eligibleBillScoutIntro = showSetupSurfaces && (isNewSetupEnabled || isControlArm);
  return <ConnectedRecurring {...props} eligibleBillScoutIntro={eligibleBillScoutIntro} />;
}
